import React, { useEffect, useRef, useState } from "react";

import Input, { InputElementType, TextareaProps } from "@common/components/Input/Input";
import useOnClickOutside from "@common/hooks/useOnClickOutside";
import { omitObjectKeys } from "@common/util/omitProps";

interface EditableTitleProps extends TextareaProps {
  defaultValue?: string;
  onEditConfirm?: (value: string) => void;
  onChangeEvent?: (value: string) => void;
  testId?: string;
  focusTrigger?: number;
}

export default function EditableTitle(props: EditableTitleProps) {
  const [value, setValue] = useState<string | undefined>(props.value?.toString() ?? props.defaultValue);
  const inputWrapperRef = useRef<HTMLTextAreaElement>(null);

  useOnClickOutside(inputWrapperRef, handleConfirmEvent);

  useEffect(() => {
    setValue(props.value?.toString());
  }, [props.value]);

  useEffect(() => {
    if (!props.autoFocus) return;
    const timer = setTimeout(() => inputWrapperRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, [props.autoFocus, props.focusTrigger]);

  const handleKeydownEvent = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.stopPropagation();
      inputWrapperRef?.current?.blur();
      handleConfirmEvent();
    }
  };

  function handleConfirmEvent() {
    if (value === props.value) return;

    if (!value) {
      setValue(props.defaultValue);
      props.onEditConfirm?.(props.defaultValue ?? "");
    } else {
      props.onEditConfirm?.(value);
    }
  }

  const handleChangeEvent = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    props.onChangeEvent?.(event.target.value);
    setValue(event.target.value);
  };

  return (
    <Input
      {...omitObjectKeys(props, ["defaultValue", "onEditConfirm", "onChangeEvent"])}
      elementType={InputElementType.TEXTAREA}
      value={value}
      innerRef={inputWrapperRef}
      onKeyDown={handleKeydownEvent}
      onChange={handleChangeEvent}
      testId={props.testId}
      maxLength={props.maxLength}
      backgroundColor={"transparent"}
      editableTitle
    />
  );
}
